import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import moment from "moment";
import "./ProductLanguageGraph.css";

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale
);

const ProductLanguageGraph = ({ productCode, reviews, getProductOptions }) => {
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("All");

  const [fromDate, setFromDate] = useState("2024-01-01");
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  const [selectedVariant, setSelectedVariant] = useState(productCode);
  const [selectedPlatform, setSelectedPlatform] = useState("All");

  const [chartData, setChartData] = useState(null);

  // Platforms
  const availablePlatforms = [
    { code: "GYG", name: "GYG" },
    { code: "HEA", name: "Headout" },
    { code: "TIQ", name: "Tiqets" },
    { code: "CIV", name: "Civitatis" },
    { code: "VIATOR", name: "Viator" },
    { code: "MUS", name: "Musement" },
  ];

  useEffect(() => {
  setSelectedVariant(productCode);
  setSelectedLanguage("All");
  setSelectedPlatform("All");
}, [productCode]);

  // PLATFORM DETECTION LOGIC (same as ProductDetails)
  const categorizePlatform = (review) => {
    const id = String(review[1] ?? "");

    if (id.startsWith("GYG")) return "GYG";
    if (id.startsWith("HEA")) return "HEA";
    if (id.includes("TIQ")) return "TIQ"; 
    if (id.startsWith("CIV")) return "CIV";
    if (id.startsWith("MUS")) return "MUS";

    // fallback Viator
    return "VIATOR";
  };

  // Extract languages for the selected variant
  useEffect(() => {
    if (!reviews || !productCode) return;

    const langs = Array.from(
      new Set(
        reviews
          .filter((r) => {
            return selectedVariant === productCode
              ? String(r[6] ?? "").trim() === productCode.trim()
              : String(r[8] ?? "").trim() === selectedVariant.trim();
          })
          .map((r) => String(r[7] ?? "").trim().toLowerCase())
      )
    );

    setLanguageOptions(langs);
  }, [productCode, reviews, selectedVariant]);

  // Main filtering + chart builder
  useEffect(() => {
    if (!reviews || !productCode) return;

    const from = moment(fromDate, "YYYY-MM-DD").startOf("day");
    const to = moment(toDate, "YYYY-MM-DD").endOf("day");

    // STEP 1 — Filter by product/option + date + platform
    const productDateFiltered = reviews.filter((r) => {
      const reviewDate = moment(r[3], "DD/MM/YYYY");
      const dateInRange = reviewDate.isBetween(from, to, "day", "[]");

      const codeMatch =
        selectedVariant === productCode
          ? String(r[6] ?? "").trim() === productCode.trim()
          : String(r[8] ?? "").trim() === selectedVariant.trim();

      const platform = categorizePlatform(r);
      const platformMatch =
        selectedPlatform === "All" || selectedPlatform === platform;

      return codeMatch && dateInRange && platformMatch;
    });

    // STEP 2 — Filter by language
    const filtered = productDateFiltered.filter((r) => {
      const lang = String(r[7] ?? "").trim().toLowerCase();
      return selectedLanguage === "All" || lang === selectedLanguage;
    });

    // Helpers
    const sumAvg = (acc, rating) => {
      acc.total += rating;
      acc.count += 1;
      return acc;
    };

    // STEP 3 — Build monthly groups (avg rating only for selected language)
    const monthlyGroups = {};
    filtered.forEach((r) => {
      const date = moment(r[3], "DD/MM/YYYY");
      const monthKey = date.format("YYYY-MM");
      const rating = Number(r[4]);

      if (!monthlyGroups[monthKey]) monthlyGroups[monthKey] = { total: 0, count: 0 };
      sumAvg(monthlyGroups[monthKey], rating);
    });

    // STEP 4 — Monthly total review counts (all languages)
    const totalMonthlyCounts = {};
    productDateFiltered.forEach((r) => {
      const date = moment(r[3], "DD/MM/YYYY");
      const monthKey = date.format("YYYY-MM");
      if (!totalMonthlyCounts[monthKey]) totalMonthlyCounts[monthKey] = 0;
      totalMonthlyCounts[monthKey] += 1;
    });

    // STEP 5 — Sort month keys
    const monthKeysSet = new Set([
      ...Object.keys(monthlyGroups),
      ...Object.keys(totalMonthlyCounts),
    ]);

    const sortedMonths = Array.from(monthKeysSet).sort();

    // STEP 6 — Build arrays
    const labels = sortedMonths.map((m) =>
      moment(m, "YYYY-MM").format("MMM 'YY")
    );

    const averages = sortedMonths.map((m) => {
      const g = monthlyGroups[m];
      if (!g || g.count === 0) return null;
      return Number((g.total / g.count).toFixed(2));
    });

    const counts = sortedMonths.map((m) => monthlyGroups[m]?.count ?? 0);
    const totals = sortedMonths.map((m) => totalMonthlyCounts[m] ?? 0);

    setChartData({
      labels,
      _totals: totals,
      datasets: [
        {
          label: `Avg Rating (${selectedLanguage === "All"
            ? "All Languages"
            : selectedLanguage
          })`,
          data: averages,
          _counts: counts,
          fill: false,
          borderColor: "rgb(75, 192, 192)",
          tension: 0.4,
          pointRadius: 4,
          borderWidth: 1,
        },
      ],
    });
  }, [
    reviews,
    productCode,
    selectedVariant,
    selectedLanguage,
    selectedPlatform,
    fromDate,
    toDate,
  ]);

  // Chart config
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (ctx) => {
            const y = ctx.parsed?.y;
            if (y == null) return null;

            const ds = ctx.dataset;
            const idx = ctx.dataIndex;
            const count = ds._counts?.[idx] ?? 0;
            const plural = count === 1 ? "review" : "reviews";

            return `Avg Rating: ${Number(y).toFixed(2)} (${count} ${plural})`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Month",
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
  min: function(ctx) {
    const allValues = ctx.chart.data.datasets[0].data.filter(v => v != null);

    if (allValues.length === 0) return 3;

    const minValue = Math.min(...allValues);
    if (minValue >= 3) return 3;

    return Math.floor(minValue);
  },
  max: 5,
  afterDataLimits: (scale) => {
    scale.max += 0.1;
  },
  title: {
    display: true,
    text: "Average Rating",
  },
  ticks: {
    stepSize: 0.2,
    callback: (value) => Number(value).toFixed(1),
  },
}
    },
    spanGaps: false,
  };

  return (
    <div className="graph-container">
      <h3>Product Rating Graph</h3>
      <div className="graph-filters">

        {/* Date Filters */}
        <label>
          From:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="date-input"
          />
        </label>

        <label>
          To:
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="date-input"
          />
        </label>

        {/* Variant Filter */}
        {getProductOptions && (
          <label>
            Option:
            <select
              value={selectedVariant}
              onChange={(e) => setSelectedVariant(e.target.value)}
            >
              <option value={productCode}>{productCode}</option>
              {getProductOptions().map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Platform Filter */}
        <label>
          Platform:
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="All">All</option>
            {availablePlatforms.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        {/* Language */}
        <label>
          Language:
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="All">All</option>
            {languageOptions
              .filter(
                (lang) =>
                  lang !== "" &&
                  lang !== "no data" &&
                  lang !== "?"
              )
              .sort()
              .map((lang) => (
                <option key={lang} value={lang}>
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </option>
              ))}
          </select>
        </label>
      </div>

      <div className="chart-wrapper">
        {chartData ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <p>No data available for selected filters.</p>
        )}
      </div>
    </div>
  );
};

export default ProductLanguageGraph;
